import { ApolloLink } from "@apollo/client";
import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import mapValues from "lodash/mapValues";

/**
 * ISO 8601 date string regex pattern.
 * Matches strings like: 2024-01-15T10:30:00.000Z or 2024-01-15T10:30:00+02:00
 */
const ISO_DATE_PATTERN =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?([+-][0-2]\d:[0-5]\d|Z)/;

/**
 * Recursively maps values in an object or array using the provided function.
 *
 * @param obj - The object or array to traverse
 * @param fn - The function to apply to leaf values
 * @returns The transformed object/array
 */
export function mapValuesDeep<T>(obj: T, fn: (value: unknown) => unknown): T {
  if (isArray(obj)) {
    return obj.map((e) => mapValuesDeep(e, fn)) as T;
  }
  if (isPlainObject(obj)) {
    return mapValues(obj as object, (val) => mapValuesDeep(val, fn)) as T;
  }
  return fn(obj) as T;
}

/**
 * Checks if a value is an ISO 8601 date string.
 *
 * @param value - The value to check
 * @returns true if the value is a valid ISO date string
 */
export function isDateString(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.length >= 24 &&
    value.length <= 29 &&
    ISO_DATE_PATTERN.test(value)
  );
}

/**
 * Converts ISO date strings to Date objects in a nested data structure.
 *
 * @param data - The data to transform
 * @returns The data with date strings converted to Date objects
 */
export function rehydrateDates<T>(data: T): T {
  return mapValuesDeep(data, (value) =>
    isDateString(value) ? new Date(value as string) : value,
  );
}

/**
 * Apollo Link that automatically converts ISO date strings in responses to Date objects.
 * Should be placed early in the link chain to process all responses.
 */
export const rehydrateDatesLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.data) {
      response.data = rehydrateDates(response.data);
    }
    return response;
  });
});
